import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { map, mergeMap, take, takeUntil } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { authConfigIdSelector } from '../../../auth/store/auth.selectors';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { emailRegex } from '../../../global/validators/match.validator';
import { State } from '../../../store';
import { AdminRegisterPayload } from '../../../user/user.model';
import { UserService } from '../../../user/service/user.abstract.service';
import { profileSelector } from '../../../user/store/user.selectors';
import { ApplicantType } from '../../constants/permit.constants';
import { I18nString, PermitCategory } from '../../models/permit-category.model';
import { PermitTypeConfig, permitTypeSubtitle } from '../../models/permit-type.model';
import { AdminCreatePermitPayload, LocationInfo, PermitFileAttachment } from '../../models/permit.model';
import { PermitCategoryService } from '../../services/permit-category.service';
import { PermitV2Service } from '../../services/permits.abstract.service';
import { PermitDetailsStepComponent } from './permit-details-step/permit-details-step.component';
import { PermitLocationStepComponent } from './permit-location-step/permit-location-step.component';

@Component({
    selector: 'app-create-permit-dialog',
    templateUrl: './create-permit-dialog.component.html',
    styleUrls: ['./create-permit-dialog.component.scss'],
})
export class CreatePermitDialogComponent implements OnInit, OnDestroy {
    @ViewChild(PermitLocationStepComponent) locationStep: PermitLocationStepComponent | null = null;

    @ViewChild(PermitDetailsStepComponent) detailsStep: PermitDetailsStepComponent | null = null;

    readonly STEP_REQUESTER = 0;

    readonly STEP_CATEGORY = 1;

    readonly STEP_TYPE = 2;

    readonly STEP_LOCATION = 3;

    readonly STEP_RESOURCES = 4;

    readonly STEP_DETAILS = 5;

    readonly STEP_REVIEW = 6;

    readonly STEP_CREATED = 7;

    currentStep = 0;

    loading = true;

    submitting = false;

    // ─── Requester ───────────────────────────────────────────────────────────
    currentUser: any | null = null;

    selectedCitizen: any | null = null;

    isCreatingCitizen = false;

    creatingCitizen = false;

    newCitizenForm: FormGroup;

    // ─── Category / type ─────────────────────────────────────────────────────
    categories: PermitCategory[] = [];

    selectedCategory: PermitCategory | null = null;

    selectedType: PermitTypeConfig | null = null;

    // Permit types with their full config (formFields, feeConfig, …) only come back from the
    // category "edition" endpoint, not from getCategories(). Cache per category id.
    private typesByCategory: Record<string, PermitTypeConfig[]> = {};

    typesLoading = false;

    // ─── Location / details ──────────────────────────────────────────────────
    location: LocationInfo | null = null;

    hasSelectedLocation = false;

    formData: Record<string, any> = {};

    // originalName per fieldId for `file` form fields — the temp-upload endpoint only returns a
    // generated blob URL, so the browser filename has to be tracked separately to survive submission.
    // An array for `multiple: true` fields, parallel to formData's array value.
    fileNames: Record<string, string | string[]> = {};

    // The details step reports its state back as the agent types, so its input is bound to a
    // snapshot refreshed only when the step opens. Binding the live state instead would mutate
    // an already-checked binding from inside the child's own init pass.
    detailsInitialFormData: Record<string, any> = {};

    detailsInitialFiles: Record<string, string | string[]> = {};

    // ─── Result ──────────────────────────────────────────────────────────────
    createdPermitId = '';

    createdPermitObjectId = '';

    private destroy$ = new Subject<void>();

    constructor(
        private dialogRef: MatDialogRef<CreatePermitDialogComponent>,
        private permitService: PermitV2Service,
        private categoryService: PermitCategoryService,
        private userService: UserService,
        private messageService: MessageService,
        private translate: TranslateService,
        private store: Store<State>,
        private fb: FormBuilder,
    ) {}

    ngOnInit(): void {
        this.store
            .select(profileSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((profile) => {
                this.currentUser = profile;
            });

        this.loadCategories();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Populated names are localized objects ({ fr, en }); resolve to the current language. */
    localizedName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    typeSubtitle(type: PermitTypeConfig | undefined | null): string {
        return permitTypeSubtitle(type, this.translate.currentLang);
    }

    // ─── Reference data ──────────────────────────────────────────────────────

    private loadCategories(): void {
        this.loading = true;
        this.categoryService
            .getCategories()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (categories) => {
                    this.categories = (categories || []).filter((c) => c.active !== false).sort((a, b) => a.displayOrder - b.displayOrder);
                    this.loading = false;
                },
                error: () => {
                    this.categories = [];
                    this.loading = false;
                    this.messageService.error(this.translate.instant('permit.create.errors.load'));
                },
            });
    }

    /**
     * Types come from the category edition payload, which returns full PermitTypeConfig objects
     * (formFields included) despite the summary typing — same cast the config screens make.
     */
    private loadTypes(categoryId: string): void {
        if (this.typesByCategory[categoryId]) return;
        this.typesLoading = true;
        this.categoryService
            .getCategoryEdition(categoryId)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (edition) => {
                    const types = (edition.permitTypes || []) as unknown as PermitTypeConfig[];
                    this.typesByCategory[categoryId] = types.filter((t) => t.active !== false).sort((a, b) => a.displayOrder - b.displayOrder);
                    this.typesLoading = false;
                },
                error: () => {
                    this.typesByCategory[categoryId] = [];
                    this.typesLoading = false;
                    this.messageService.error(this.translate.instant('permit.create.errors.load'));
                },
            });
    }

    get currentTypes(): PermitTypeConfig[] {
        const categoryId = this.selectedCategory?._id;
        return (categoryId && this.typesByCategory[categoryId]) || [];
    }

    // ─── Requester step ──────────────────────────────────────────────────────

    onCitizenSelected(users: any[]): void {
        const user = users?.[0];
        this.selectedCitizen = user ? { ...user, picture: user.picture || user.avatar } : null;
    }

    selectCurrentUser(): void {
        if (this.currentUser) {
            this.selectedCitizen = this.currentUser;
        }
        this.next();
    }

    isCurrentUserSelected(): boolean {
        if (!this.selectedCitizen || !this.currentUser) return false;
        const citizenId = this.selectedCitizen.id || this.selectedCitizen._id;
        return citizenId === this.currentUser.id || citizenId === this.currentUser._id;
    }

    onCitizenCreateClick(searchTerm = ''): void {
        this.isCreatingCitizen = true;

        let initialEmail = '';
        let initialPhone = '';
        if (searchTerm) {
            if (searchTerm.includes('@')) {
                initialEmail = searchTerm;
            } else if (/\d/.test(searchTerm)) {
                initialPhone = searchTerm;
            }
        }

        this.newCitizenForm = this.fb.group({
            firstName: ['', [Validators.required]],
            lastName: ['', [Validators.required]],
            email: [initialEmail, [Validators.required, Validators.pattern(emailRegex)]],
            phone: [initialPhone, [Validators.minLength(14), Validators.maxLength(14)]],
        });
    }

    cancelCitizenCreation(): void {
        this.isCreatingCitizen = false;
        this.newCitizenForm = null;
    }

    submitNewCitizen(): void {
        if (this.newCitizenForm.invalid) {
            this.newCitizenForm.markAllAsTouched();
            return;
        }

        this.creatingCitizen = true;
        const formValue = this.newCitizenForm.value;

        this.store
            .select(authConfigIdSelector)
            .pipe(take(1))
            .subscribe((clientId) => {
                const registerPayload: AdminRegisterPayload = {
                    firstName: formValue.firstName,
                    lastName: formValue.lastName,
                    email: formValue.email,
                    orgId: environment.OIDC_ORG_ID,
                    clientId: clientId || undefined,
                };

                this.userService
                    .adminRegisterUser(registerPayload)
                    .pipe(
                        mergeMap((response) => {
                            if (!response?.userId) {
                                throw new Error('No userId returned');
                            }
                            if (formValue.phone) {
                                return this.userService.updateUserRecordById(response.userId, { phone: formValue.phone }).pipe(map(() => response));
                            }
                            return of(response);
                        }),
                        takeUntil(this.destroy$),
                    )
                    .subscribe({
                        next: (response) => this.handleUserCreated(response.userId),
                        error: (error) => {
                            this.creatingCitizen = false;
                            const key = error?.status === 409 ? 'permit.create.errors.email_already_used' : 'permit.create.errors.user_creation_failed';
                            this.messageService.error(this.translate.instant(key));
                        },
                    });
            });
    }

    private handleUserCreated(userId: string): void {
        this.userService
            .getUser(userId)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (user: any) => {
                    this.selectedCitizen = {
                        id: user._id,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email,
                        phone: user.phone,
                        picture: user.picture,
                    };
                    this.creatingCitizen = false;
                    this.isCreatingCitizen = false;
                },
                error: () => {
                    this.creatingCitizen = false;
                    this.isCreatingCitizen = false;
                    this.messageService.error(this.translate.instant('permit.create.errors.user_creation_failed'));
                },
            });
    }

    /** The requester's user id, in whichever shape the picker handed them over. */
    get requesterId(): string | null {
        return this.selectedCitizen?.id || this.selectedCitizen?._id || null;
    }

    citizenName(citizen: any): string {
        if (!citizen) return '';
        const composed = `${citizen.firstName || ''} ${citizen.lastName || ''}`.trim();
        return composed || citizen.name || '';
    }

    // ─── Category / type steps ───────────────────────────────────────────────

    onCategorySelected(category: PermitCategory): void {
        const changed = this.selectedCategory?._id !== category._id;
        this.selectedCategory = category;
        if (changed) {
            this.selectedType = null;
            this.formData = {};
        }
        this.loadTypes(category._id);
        this.next();
    }

    onTypeSelected(type: PermitTypeConfig): void {
        if (this.selectedType?._id !== type._id) {
            this.formData = {};
        }
        this.selectedType = type;
        this.next();
    }

    // ─── Location step ───────────────────────────────────────────────────────

    onLocationSelectionChanged(hasSelection: boolean): void {
        this.hasSelectedLocation = hasSelection;
    }

    onLocationChanged(location: LocationInfo | null): void {
        this.location = location;
    }

    skipLocation(): void {
        this.location = null;
        this.hasSelectedLocation = false;
        this.onStepChange(this.selectedType?.files?.length ? this.STEP_RESOURCES : this.STEP_DETAILS);
    }

    // ─── Details step ────────────────────────────────────────────────────────

    onDetailsChanged(formData: Record<string, any>): void {
        this.formData = formData;
    }

    onDetailsFilesChanged(fileNames: Record<string, string | string[]>): void {
        this.fileNames = fileNames;
    }

    // ─── Navigation ──────────────────────────────────────────────────────────

    onStepChange(step: number): void {
        if (step === this.STEP_DETAILS) {
            this.detailsInitialFormData = { ...this.formData };
            this.detailsInitialFiles = { ...this.fileNames };
        }
        this.currentStep = step;
    }

    isStepReachable(step: number): boolean {
        // RESOURCES is skipped when the type has no files, so its dot must not be
        // clickable even though it's numerically behind the current step.
        if (step === this.STEP_RESOURCES && !this.selectedType?.files?.length) return false;
        return step < this.currentStep;
    }

    goToStep(step: number): void {
        if (this.isStepReachable(step)) {
            this.onStepChange(step);
        }
    }

    goBack(): void {
        if (this.currentStep === this.STEP_DETAILS && this.detailsStep && !this.detailsStep.isOnFirstSubStep()) {
            this.detailsStep.goToPreviousSubStep();
            return;
        }
        if (this.currentStep === this.STEP_DETAILS && !this.selectedType?.files?.length) {
            this.onStepChange(this.STEP_LOCATION);
        } else if (this.currentStep > this.STEP_REQUESTER) {
            this.onStepChange(this.currentStep - 1);
        }
    }

    next(): void {
        switch (this.currentStep) {
            case this.STEP_REQUESTER:
                this.onStepChange(this.STEP_CATEGORY);
                break;
            case this.STEP_CATEGORY:
                this.onStepChange(this.STEP_TYPE);
                break;
            case this.STEP_TYPE:
                this.onStepChange(this.STEP_LOCATION);
                break;
            case this.STEP_LOCATION:
                this.location = this.locationStep ? this.locationStep.getLocation() : this.location;
                this.onStepChange(this.selectedType?.files?.length ? this.STEP_RESOURCES : this.STEP_DETAILS);
                break;
            case this.STEP_RESOURCES:
                this.onStepChange(this.STEP_DETAILS);
                break;
            case this.STEP_DETAILS:
                if (this.detailsStep) {
                    if (!this.detailsStep.isOnLastSubStep()) {
                        if (!this.detailsStep.isCurrentSubStepValid()) {
                            this.detailsStep.markCurrentSubStepAsTouched();
                            return;
                        }
                        this.detailsStep.goToNextSubStep();
                        return;
                    }
                    if (!this.detailsStep.isValid()) {
                        this.detailsStep.markAllAsTouched();
                        return;
                    }
                    this.onDetailsChanged(this.detailsStep.collect());
                }
                this.onStepChange(this.STEP_REVIEW);
                break;
            case this.STEP_REVIEW:
                this.submitPermit();
                break;
            default:
        }
    }

    canProceed(): boolean {
        if (this.currentStep === this.STEP_REQUESTER) return !!this.selectedCitizen && !this.isCreatingCitizen;
        if (this.currentStep === this.STEP_CATEGORY) return !!this.selectedCategory;
        if (this.currentStep === this.STEP_TYPE) return !!this.selectedType;
        if (this.currentStep === this.STEP_LOCATION) return this.hasSelectedLocation;
        if (this.currentStep === this.STEP_RESOURCES) return true;
        // Always enabled: clicking Next while invalid marks the current sub-step touched (revealing
        // errors) instead of leaving a disabled button with no visible explanation.
        if (this.currentStep === this.STEP_DETAILS) return true;
        if (this.currentStep === this.STEP_REVIEW) return !this.submitting;
        return false;
    }

    // ─── Submit ──────────────────────────────────────────────────────────────

    /**
     * `file` fields upload as soon as they're picked, so their URLs already sit in formData at
     * this point. The permit still needs to exist before registering their original filenames —
     * see attachPendingFiles().
     */
    private submitPermit(): void {
        if (this.submitting) return;
        this.submitting = true;

        const payload: AdminCreatePermitPayload = {
            citizenUserId: this.requesterId,
            // The selected citizen is the applicant: the counter agent files on their behalf.
            applicantType: ApplicantType.SELF,
            permitType: this.selectedType?._id || '',
            formData: this.formData || {},
        };

        if (this.location) {
            payload.location = this.location;
        }

        this.permitService
            .createPermit(payload)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.createdPermitId = response?.permitId || '';
                    this.createdPermitObjectId = response?._id || '';
                    this.attachPendingFiles(this.createdPermitObjectId);
                },
                error: (error) => this.handleSubmitError(error),
            });
    }

    /**
     * The temp-upload endpoint used by the details step's file fields only returns a generated
     * blob URL, so the browser filename collected in `fileNames` has to be registered separately
     * once the permit exists, or the permit ends up with a blob hash for a filename everywhere
     * it's displayed.
     */
    private attachPendingFiles(permitId: string): void {
        const files = this.collectFileAttachments();
        if (!permitId || !files.length) {
            this.submitting = false;
            this.onStepChange(this.STEP_CREATED);
            return;
        }
        this.permitService
            .attachFiles(permitId, { files })
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.submitting = false;
                    this.onStepChange(this.STEP_CREATED);
                },
                error: () => {
                    this.submitting = false;
                    this.messageService.error(this.translate.instant('permit.create.errors.attach_files'));
                    this.onStepChange(this.STEP_CREATED);
                },
            });
    }

    private collectFileAttachments(): PermitFileAttachment[] {
        return Object.keys(this.fileNames).reduce((files: PermitFileAttachment[], fieldId): PermitFileAttachment[] => {
            const value = this.formData[fieldId];
            if (Array.isArray(value)) {
                const names = this.fileNames[fieldId];
                const namesArr = Array.isArray(names) ? names : [];
                const entries = value
                    .map((url: string, i: number): PermitFileAttachment => ({ url, originalName: namesArr[i] ?? '', fieldId }))
                    .filter((file) => typeof file.url === 'string' && file.url.length > 0 && !!file.originalName);
                return files.concat(entries);
            }
            const originalName = this.fileNames[fieldId];
            const file: PermitFileAttachment = { url: value, originalName: typeof originalName === 'string' ? originalName : '', fieldId };
            return typeof file.url === 'string' && file.url.length > 0 && !!file.originalName ? files.concat([file]) : files;
        }, []);
    }

    private handleSubmitError(error: any): void {
        this.submitting = false;
        let key = 'permit.create.errors.generic';
        if (error?.status === 400) key = 'permit.create.errors.invalid_data';
        else if (error?.status === 401 || error?.status === 403) key = 'permit.create.errors.unauthorized';
        else if (error?.status >= 500) key = 'permit.create.errors.server';
        this.messageService.error(this.translate.instant(key));
    }

    // ─── Close ───────────────────────────────────────────────────────────────

    closeDialog(): void {
        if (this.createdPermitObjectId || this.createdPermitId) {
            this.closeCreated();
            return;
        }
        this.dialogRef.close();
    }

    closeCreated(): void {
        this.dialogRef.close({
            refresh: true,
            createdPermitId: this.createdPermitId,
            createdPermitObjectId: this.createdPermitObjectId,
        });
    }
}
